import ArrowBackIcon                       from '@/assets/icons/ArrowBackIcon';
import CalendarIcon                        from '@/assets/icons/CalendarIcon';
import PageContainer                       from '@/components/pageContainer/PageContainer';
import PageTitle                           from '@/components/pageTitle/PageTitle';
import useTranslations                     from '@/hooks/useTranslations';
import EntityModel                         from '@/models/services/fondations/EntityModel';
import StandModel                          from '@/models/services/website/StandModel';
import NotFoundPage                        from '@/pages/NotFound/NotFoundPage';
import EditStandForm                       from '@/pages/Stand/components/EditStandForm';
import { Alert }                           from '@chakra-ui/react';
import { Box }                             from '@chakra-ui/react';
import useFetch                            from '@widesk-core/hooks/useFetch';
import { useTranslation }                  from 'react-i18next';
import { Link }                            from 'react-router-dom';
import { useParams }                       from 'react-router-dom';
import Spinner                             from '@/components/spinner/Spinner';
import PagedCollection                     from '@widesk-core/models/PagedCollection';
import ApiCollection                       from '@widesk-core/models/ApiCollection';
import StandPageModel                      from '@/models/services/website/StandPageModel';
import StandPageValidationStatusModel      from '@/models/services/website/StandPageValidationStatusModel';
import WebsiteConfigModel                  from '@/models/services/website/WebsiteConfigModel';
import { getIdFromUrn }                    from '@widesk-core/tools/identifier';
import { selectStandPageToEdit }           from '@/pages/Stand/hooks/standPageSelection';

export type EditStandPageData = {
	stand: StandModel,
	standPage: StandPageModel | null,
	corporateName: string,
	validationStatuses: StandPageValidationStatusModel[],
};

const EditStandPage = () => {

	const { t, i18n } = useTranslation();
	const { getNavigationTranslation } = useTranslations();

	const { standId } = useParams();

	const { data, isLoading } = useFetch(async () => {
		const standCollection = await new PagedCollection(StandModel)
			.setItemsPerPage(1)
			.setFilter('id', [(standId as string)])
			.list();

		await Promise.all([
			standCollection.whenIsLoaded(s => s.websiteEntertainment),
			standCollection.whenIsLoaded(s => s.exhibitor),
		]);
		
		const stand = standCollection.first();

		const corporateName = stand?.exhibitor?.entityUrn
			? (await new EntityModel({ id: getIdFromUrn(stand.exhibitor.entityUrn as Urn) }).fetch()).get('corporateName', '') as string
			: '';

		const websiteConfigCollection = new PagedCollection(WebsiteConfigModel)
			.setItemsPerPage(1)
			.setFilter('website', [(stand?.websiteEntertainment.website.id as string)])
			.setFilter('reference', 'stand_edit_end');

		const standPageCollection = new ApiCollection(StandPageModel)
			.setSort('id', 'desc')
			.setFilter('stand', [(stand?.id as string)]);

		const validationStatusCollection = new ApiCollection(StandPageValidationStatusModel);

		await Promise.all([
			standPageCollection.list(),
			validationStatusCollection.list(),
			websiteConfigCollection.list(),
		]);

		const websiteConfig = websiteConfigCollection.first();
		const validationStatuses = validationStatusCollection.models;
		const standPage = selectStandPageToEdit(standPageCollection.models, validationStatuses);

		return {
			stand: stand,
			standPage: standPage,
			websiteConfig: websiteConfig,
			corporateName: corporateName,
			validationStatuses: validationStatuses,
		};
	}, true);

	if (isLoading) {
		return <Spinner />;
	}

	if (!data?.stand?.id) {
		return <NotFoundPage />;
	}

	const isDeadlinePassed = !!data.websiteConfig?.value && new Date() > new Date(data.websiteConfig.value + 'T23:59:59');

	return (
		<PageContainer>
			<Link to={getNavigationTranslation('stands', 'path')} style={{ width: "fit-content" }}>
				<Box
					display="flex"
					alignItems="center"
					gap="15px"
					width="fit-content"
					lineHeight="0px"
					pt="10px"
					pb="10px"
				>
					<ArrowBackIcon style={{ marginBottom: "0.5px" }} size="lg" />
					<PageTitle>
						{t('pages.editStand.label')}
					</PageTitle>
				</Box>
			</Link>
			<Box pb="10px">
				{isDeadlinePassed ? (
					<Alert.Root status="error" alignItems="center" boxShadow="sm">
						<CalendarIcon size="lg"/>
						<Alert.Title>
							{t('pages.editStand.form.deadlinePassedBanner')}
						</Alert.Title>
					</Alert.Root>
				) : (
					<Alert.Root status="warning" alignItems="center" boxShadow="sm">
						<CalendarIcon size="lg"/>
						<Alert.Title>
							{t('pages.editStand.alert.message', {
									date: data.websiteConfig?.value
										? new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(data.websiteConfig.value + 'T00:00:00'))
										: '',
								})}
						</Alert.Title>
					</Alert.Root>
				)}
			</Box>
			<EditStandForm data={(data as EditStandPageData)} isDeadlinePassed={isDeadlinePassed} />
		</PageContainer>
	);
}

export default EditStandPage;